// Deterministic finding IDs. Re-analyses of the same manuscript produce the
// same IDs, so editor confirm/dismiss actions persist across sessions.
//
// We use djb2 — fast, synchronous, collision-tolerant for our scale. The IDs
// are not security-sensitive (the audit log's SHA-256 chain is what enforces
// tamper detection); they only need to be stable + locally unique.

function djb2(input: string): number {
  let h = 5381;
  for (let i = 0; i < input.length; i++) {
    h = ((h << 5) + h + input.charCodeAt(i)) & 0xffffffff;
  }
  return h >>> 0;
}

/**
 * Stable ID for a finding derived from its category + text + offset.
 * Two calls with the same inputs return the same string.
 */
export function stableFindingId(category: string, text: string, offset: number): string {
  const h = djb2(`${category}|${text}|${offset}`);
  return `f-${category[0] ?? 'x'}-${h.toString(36)}`;
}

/** Specialised stable ID for methodology findings (pattern layer). */
export function methodologyFindingId(itemId: string, status: 'located' | 'not_located'): string {
  return `meth-${status}-${itemId}`;
}

/** Specialised stable ID for LLM-graded methodology findings. */
export function llmMethodologyFindingId(itemId: string, status: string): string {
  return `llm-meth-${status}-${itemId}`;
}

/** Specialised stable ID for the LLM spin finding (only one per skim). */
export function llmSpinFindingId(level: string): string {
  return `llm-spin-${level}`;
}

export type ApiRefStatusKind = 'not_found' | 'retracted' | 'mismatch';

/** Specialised stable ID for an API-layer reference-audit finding. */
export function apiRefFindingId(doi: string, status: ApiRefStatusKind): string {
  // DOIs can contain '/' and ':' — fine in IDB keys; we keep them raw for readability.
  return `apiref-${status}-${doi}`;
}
